import React, {
  createContext, useState, useContext, useEffect, useCallback, useMemo,
} from 'react';
import { DIAMOND_TYPES } from '../constants/appConstants';

const CartActionsContext = createContext(null);
const CartDataContext = createContext(null);

/** @deprecated Merged view; prefer useCartActions / useCartData to limit re-renders. */
const CartContext = createContext(null);

const CART_STORAGE_KEY = 'diamondsup_cart';

const loadCart = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    // Migration: stale carts may still carry `reservedCarat` from before the
    // unified pricing rename. Forward it to `weight` so checkout payloads work.
    return Array.isArray(parsed)
      ? parsed.map((it) =>
          it && it.weight == null && it.reservedCarat != null
            ? { ...it, weight: it.reservedCarat }
            : it
        )
      : [];
  } catch {
    return [];
  }
};

const saveCart = (items) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch {
    // silently fail
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(loadCart);

  useEffect(() => {
    saveCart(cartItems);
  }, [cartItems]);

  const addToCart = useCallback((item) => {
    setCartItems((prev) => {
      // Prevent duplicates
      if (item.type === DIAMOND_TYPES.CERTIFIED) {
        if (prev.some((i) => i.type === DIAMOND_TYPES.CERTIFIED && i.diamond?._id === item.diamond?._id)) {
          return prev;
        }
      }
      // For non-certified, allow same lot with different carat amounts
      return [...prev, { ...item, addedAt: new Date().toISOString() }];
    });
  }, []);

  const removeFromCart = useCallback((index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateCartItem = useCallback((index, updates) => {
    setCartItems((prev) => prev.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  /**
   * Keeps only the items at the given indices, removing everything else.
   * Used after a partial checkout to retain items that failed (e.g. limit exceeded)
   * while removing items that were successfully submitted.
   */
  const retainItemsByIndices = useCallback((indicesToKeep) => {
    setCartItems((prev) => prev.filter((_, i) => indicesToKeep.includes(i)));
  }, []);

  const actions = useMemo(() => ({
    addToCart,
    removeFromCart,
    updateCartItem,
    clearCart,
    retainItemsByIndices,
  }), [addToCart, removeFromCart, updateCartItem, clearCart, retainItemsByIndices]);

  const cartData = useMemo(() => {
    const certifiedIds = new Set(
      cartItems
        .filter((i) => i.type === DIAMOND_TYPES.CERTIFIED && i.diamond?._id)
        .map((i) => i.diamond._id),
    );
    return {
      cartItems,
      cartCount: cartItems.length,
      isInCart: (diamondId) => certifiedIds.has(diamondId),
    };
  }, [cartItems]);

  const merged = useMemo(
    () => ({ ...actions, ...cartData }),
    [actions, cartData],
  );

  return (
    <CartActionsContext.Provider value={actions}>
      <CartDataContext.Provider value={cartData}>
        <CartContext.Provider value={merged}>
          {children}
        </CartContext.Provider>
      </CartDataContext.Provider>
    </CartActionsContext.Provider>
  );
};

export const useCartActions = () => {
  const context = useContext(CartActionsContext);
  if (!context) {
    throw new Error('useCartActions must be used within a CartProvider');
  }
  return context;
};

export const useCartData = () => {
  const context = useContext(CartDataContext);
  if (!context) {
    throw new Error('useCartData must be used within a CartProvider');
  }
  return context;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
